import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  Renderer2,
} from "@angular/core";

export interface DroppedFile {
  file: File;
  fileName: string;
  fileSize: number;
  fileType: string;
  fileExtension: string;
}

@Directive({
  selector: "[jniGlobalFileDrop]",
  standalone: true,
})
export class GlobalFileDragDropDirective {
  @Input() acceptedTypes?: string[]; // e.g., ['pdf', 'jpg', 'png']
  @Input() maxFileSize?: number; // in MB, default 30MB
  @Input() maxFiles?: number; // default 10
  @Input() disabled?: boolean = false;

  @Output() filesDropped = new EventEmitter<DroppedFile[]>();
  @Output() filesRejected = new EventEmitter<
    { file: File; reason: string }[]
  >();
  @Output() dragStateChanged = new EventEmitter<boolean>();

  private isDragOver = false;
  private dragCounter = 0;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  @HostListener("dragenter", ["$event"])
  onDragEnter(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;

    if (!this.isDragOver) {
      this.isDragOver = true;
      this.renderer.addClass(this.el.nativeElement, "jni-drag-over");
      this.dragStateChanged.emit(true);
    }
  }

  @HostListener("dragover", ["$event"])
  onDragOver(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener("dragleave", ["$event"])
  onDragLeave(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;

    if (this.dragCounter === 0) {
      this.isDragOver = false;
      this.renderer.removeClass(this.el.nativeElement, "jni-drag-over");
      this.dragStateChanged.emit(false);
    }
  }

  @HostListener("drop", ["$event"])
  onDrop(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    this.dragCounter = 0;
    this.isDragOver = false;
    this.renderer.removeClass(this.el.nativeElement, "jni-drag-over");
    this.dragStateChanged.emit(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(files);
    }
  }

  private processFiles(fileList: FileList): void {
    const validFiles: DroppedFile[] = [];
    const rejectedFiles: { file: File; reason: string }[] = [];
    const maxSize = (this.maxFileSize ?? 30) * 1024 * 1024; // Convert MB to bytes
    const maxCount = this.maxFiles ?? 10;

    // Check file count
    if (fileList.length > maxCount) {
      for (const file of fileList) {
        rejectedFiles.push({
          file,
          reason: `Maximum ${maxCount} files allowed`,
        });
      }
      this.filesRejected.emit(rejectedFiles);
      return;
    }

    for (const file of fileList) {
      const fileExtension = this.getFileExtension(file.name);

      // Check file size
      if (file.size > maxSize) {
        rejectedFiles.push({
          file,
          reason: `File size exceeds ${this.maxFileSize ?? 30}MB limit`,
        });
        continue;
      }

      // Check file type if specified
      if (this.acceptedTypes && this.acceptedTypes.length > 0) {
        const isAccepted = this.acceptedTypes.some(
          type =>
            fileExtension.toLowerCase() === type.toLowerCase() ||
            file.type.includes(type.toLowerCase())
        );

        if (!isAccepted) {
          rejectedFiles.push({
            file,
            reason: `File type not accepted. Allowed: ${this.acceptedTypes.join(", ")}`,
          });
          continue;
        }
      }

      // File is valid
      validFiles.push({
        file,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        fileExtension,
      });
    }

    if (validFiles.length > 0) {
      this.filesDropped.emit(validFiles);
    }

    if (rejectedFiles.length > 0) {
      this.filesRejected.emit(rejectedFiles);
    }
  }

  private getFileExtension(fileName: string): string {
    return fileName.split(".").pop()?.toLowerCase() ?? "";
  }
}
